import { useBrand } from '../lib/brand.ts';
import { lazy, Suspense, useEffect, useRef, useState, type FormEvent } from 'react';
import { Icon } from '../components/Icon.tsx';
import { BrandMark } from '../components/BrandMark.tsx';
import { LanguageSwitch } from '../components/LanguageSwitch.tsx';
import { Button, Field, Keypad } from '../components/ui.tsx';
import { t } from '../i18n/index.ts';
import { ApiError, request } from '../lib/api.ts';
import { takeIdleNote, useSession } from '../lib/session.tsx';
import { go, otherAccounts, restore } from '../lib/accounts.ts';
import { VENDOR } from '../lib/vendor.ts';

/** The six promises beside the sign-in form: icon, title, one line. */
const FEATURES = [
  ['shieldCheck', 'login.f_security', 'login.f_security_d'],
  ['chartUp', 'login.f_visibility', 'login.f_visibility_d'],
  ['layers', 'login.f_control', 'login.f_control_d'],
  ['users', 'login.f_companies', 'login.f_companies_d'],
  ['cloud', 'login.f_deploy', 'login.f_deploy_d'],
  ['globe', 'login.f_global', 'login.f_global_d'],
] as const;

/** What the platform covers, as outlined badges at the foot of the panel. */
const MODULES = ['login.m_finance', 'login.m_sales', 'login.m_inventory', 'login.m_projects', 'login.m_hr', 'login.m_pos'] as const;

/** The 3D globe is a separate file, fetched only where the branding panel is shown in full. */
const Globe = lazy(() => import('../components/Globe.tsx'));
const WIDE = '(min-width: 861px)';

function useWide() {
  const [wide, setWide] = useState(() => window.matchMedia(WIDE).matches);
  useEffect(() => {
    const mq = window.matchMedia(WIDE);
    const on = () => setWide(mq.matches);
    mq.addEventListener('change', on);
    return () => mq.removeEventListener('change', on);
  }, []);
  return wide;
}

const MODE_KEY = 'erp.login_mode';
type Mode = 'pin' | 'password';
const savedMode = (): Mode => {
  try {
    return localStorage.getItem(MODE_KEY) === 'password' ? 'password' : 'pin';
  } catch {
    return 'pin';
  }
};
const saveMode = (m: Mode) => {
  try {
    localStorage.setItem(MODE_KEY, m);
  } catch {
    // The PIN pad shows first next time.
  }
};

export function Login() {
  const { signIn, tenant } = useSession();
  const [business, setBusiness] = useState(tenant?.slug ?? appBusiness() ?? '');
  const [username, setUsername] = useState('');
  const [pin, setPin] = useState('');
  // Staff sign in with a PIN; the people who run the business with a password and a code.
  const [mode, setMode] = useState<Mode>(savedMode);
  const [password, setPassword] = useState('');
  const [step, setStep] = useState<'who' | 'code'>('who');
  const [code, setCode] = useState('');
  const [rememberDevice, setRememberDevice] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(() => {
    const minutes = takeIdleNote();
    return minutes ? t('login.signed_out_idle', { minutes }) : null;
  });
  const [busy, setBusy] = useState(false);
  const userRef = useRef<HTMLInputElement>(null);
  const codeRef = useRef<HTMLInputElement>(null);
  // Businesses still signed in on this device (after "Add a business"): one press goes back to them.
  const [kept] = useState(otherAccounts);
  const sso = useSso(business);
  useEffect(() => {
    userRef.current?.focus();
  }, []);
  useEffect(() => {
    if (step === 'code') codeRef.current?.focus();
  }, [step]);

  const switchMode = (m: Mode) => {
    setMode(m);
    saveMode(m);
    setError(null);
    setNotice(null);
  };
  const secret = mode === 'pin' ? pin : password;

  const submit = async (e?: FormEvent) => {
    e?.preventDefault();
    if (!business || !username || (mode === 'pin' ? pin.length < 4 : !password)) {
      setError(t(mode === 'pin' ? 'login.missing' : 'login.missing_password'));
      return;
    }
    if (step === 'code' && code.replace(/[\s-]/g, '').length < 6) {
      setError(t('login.code_missing'));
      return;
    }
    setBusy(true);
    setError(null);
    try {
      await signIn(business, username, secret, step === 'code' ? { code, remember: rememberDevice } : {});
    } catch (err) {
      const details = err instanceof ApiError ? err.details : undefined;
      if (details?.need_code) {
        // The password was right: the code from the app comes next.
        setStep('code');
        setNotice(null);
        return;
      }
      if (details?.sso_only) {
        setPin('');
        setPassword('');
        setNotice(t('login.sso_only'));
        return;
      }
      if (details?.password_required) {
        switchMode('password');
        setPin('');
        setNotice(t('login.use_password'));
        return;
      }
      if (step === 'code') setCode('');
      else if (mode === 'pin') setPin('');
      setError(err instanceof ApiError && err.code !== 'offline' ? err.message : t('error.offline'));
    } finally {
      setBusy(false);
    }
  };

  const back = () => {
    setStep('who');
    setCode('');
    setError(null);
  };

  const onKey = (k: string) => {
    setError(null);
    if (k === 'back') setPin((p) => p.slice(0, -1));
    else if (k === 'go') void submit();
    else setPin((p) => (p.length < 12 ? p + k : p));
  };

  return (
    <main className="login login-pro">
      <BrandPanel />

      <form className="login-card" onSubmit={submit} noValidate>
        <div className="login-head">
          <h1>{t(step === 'code' ? 'login.code_title' : 'login.title')}</h1>
          <LanguageSwitch />
        </div>
        {step === 'code' ? (
          <>
            <p className="muted">{t('login.code_intro', { name: username })}</p>
            <Field label={t('login.code')} hint={t('login.code_hint')}>
              <input
                ref={codeRef}
                className="input code-input"
                inputMode="numeric"
                autoComplete="one-time-code"
                autoCapitalize="none"
                spellCheck={false}
                maxLength={12}
                value={code}
                onChange={(e) => {
                  setError(null);
                  setCode(e.target.value.replace(/[^0-9a-zA-Z-\s]/g, '').slice(0, 12));
                }}
              />
            </Field>
            <label className="check-row">
              <input type="checkbox" checked={rememberDevice} onChange={(e) => setRememberDevice(e.target.checked)} />
              <span>{t('login.remember_device')}</span>
            </label>
          </>
        ) : (
          <>
            <p className="muted">{t(mode === 'pin' ? 'login.subtitle' : 'login.subtitle_password')}</p>
            <Field label={t('login.business')} hint={t('login.business_hint')}>
              <input
                className="input"
                value={business}
                onChange={(e) => setBusiness(e.target.value)}
                autoCapitalize="none"
                autoCorrect="off"
                spellCheck={false}
                autoComplete="organization"
              />
            </Field>
            <Field label={t('login.username')}>
              <input
                ref={userRef}
                className="input"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                autoCapitalize="none"
                autoCorrect="off"
                spellCheck={false}
                autoComplete="username"
              />
            </Field>
            {mode === 'pin' ? (
              <>
                <Field label={t('login.pin')}>
                  <input
                    className="input pin-input"
                    type="password"
                    inputMode="numeric"
                    autoComplete="current-password"
                    value={pin}
                    onChange={(e) => {
                      setError(null);
                      setPin(e.target.value.replace(/\D/g, '').slice(0, 12));
                    }}
                    aria-describedby="pin-dots"
                  />
                </Field>
                <div id="pin-dots" className="pin-dots" aria-hidden="true">
                  {Array.from({ length: Math.max(4, pin.length) }, (_, i) => (
                    <span key={i} className={i < pin.length ? 'on' : ''} />
                  ))}
                </div>
                <Keypad onKey={onKey} />
              </>
            ) : (
              <Field label={t('login.password')}>
                <input
                  className="input"
                  type="password"
                  autoComplete="current-password"
                  maxLength={200}
                  value={password}
                  onChange={(e) => {
                    setError(null);
                    setPassword(e.target.value);
                  }}
                />
              </Field>
            )}
          </>
        )}
        {notice ? (
          <p className="form-note" role="status">
            <Icon name="info" size={16} /> {notice}
          </p>
        ) : null}
        {error ? (
          <p className="form-error" role="alert">
            <Icon name="alert" size={16} /> {error}
          </p>
        ) : null}
        <Button variant="primary" size="lg" type="submit" busy={busy} className="w-full">
          {t(step === 'code' ? 'login.verify' : 'login.submit')}
        </Button>
        {step === 'who' && sso ? (
          <>
            <p className="login-or" aria-hidden="true">
              {t('login.or')}
            </p>
            <Button variant="secondary" size="lg" icon="key" className="w-full" onClick={() => startSso(business)}>
              {t('login.sso', { label: sso })}
            </Button>
          </>
        ) : null}
        {step === 'code' ? (
          <Button variant="ghost" icon="back" className="w-full" onClick={back}>
            {t('common.back')}
          </Button>
        ) : (
          <button type="button" className="link login-mode" onClick={() => switchMode(mode === 'pin' ? 'password' : 'pin')}>
            <Icon name={mode === 'pin' ? 'key' : 'hash'} size={16} /> {t(mode === 'pin' ? 'login.with_password' : 'login.with_pin')}
          </button>
        )}
        {step === 'code' ? null : (
          <p className="login-signup">
            {t('signup.new_here')}{' '}
            <a className="link" href="/signup">
              {t('signup.start_trial')}
            </a>
          </p>
        )}
        {kept.length ? (
          <nav className="login-kept" aria-label={t('biz.back_to')}>
            <p className="muted small">{t('biz.back_to')}</p>
            {kept.map((a) => (
              <Button key={a.key} variant="ghost" icon="store" className="w-full" onClick={() => restore(a.key) && go()}>
                {a.business} · {a.user}
              </Button>
            ))}
          </nav>
        ) : null}
      </form>
      <BrandFooter />
    </main>
  );
}

/** Who offers the product: ERP PRO's owner, or on a partner's address the partner's brand and website. */
function OwnerLine() {
  const brand = useBrand();
  if (brand) {
    const site = brand.website?.replace(/^https?:\/\//, '').replace(/\/$/, '');
    return <p className="login-owner">{site ? `${brand.name} · ${site}` : brand.name}</p>;
  }
  return (
    <p className="login-owner">
      {t('login.owner', { company: VENDOR.legal_name })} · {VENDOR.product.website}
    </p>
  );
}

/** On a partner's address (white-label): how to reach the partner, and "Powered by ERP PRO" unless it is hidden. */
function BrandFooter() {
  const brand = useBrand();
  if (!brand) return null;
  const contact = [brand.support_email, brand.support_phone].filter(Boolean);
  if (!contact.length && !brand.powered_by) return null;
  return (
    <footer className="login-brand-foot small">
      {contact.length ? (
        <span>
          {t('login.help')}{' '}
          {brand.support_email ? (
            <a className="link" href={`mailto:${brand.support_email}`}>
              {brand.support_email}
            </a>
          ) : null}
          {brand.support_email && brand.support_phone ? ' · ' : null}
          {brand.support_phone ? (
            <a className="link" href={`tel:${brand.support_phone.replace(/[^+\d]/g, '')}`}>
              {brand.support_phone}
            </a>
          ) : null}
        </span>
      ) : null}
      {/* Said in ERP PRO's own name: the brand never replaces it here. */}
      {brand.powered_by ? <span className="muted">Powered by ERP PRO</span> : null}
    </footer>
  );
}

/** An app's "Connect ERP PRO" link names its business: the sign-in page starts with it. */
function appBusiness(): string | null {
  if (!window.location.pathname.startsWith('/oauth/authorize')) return null;
  return /^erpc\.([a-z0-9-]{1,63})\./.exec(new URLSearchParams(window.location.search).get('client_id') ?? '')?.[1] ?? null;
}

const RETURN_KEY = 'erp.sso_return';
/** Sends the browser to the business's provider (Manage → Single sign-on), coming back to an app's "Allow" page when it started there. */
const startSso = (business: string) => {
  try {
    if (window.location.pathname.startsWith('/oauth/authorize')) sessionStorage.setItem(RETURN_KEY, window.location.pathname + window.location.search);
    else sessionStorage.removeItem(RETURN_KEY);
  } catch {
    // Without storage the person lands on the home page.
  }
  window.location.assign(`/api/auth/sso/${encodeURIComponent(business.trim().toLowerCase())}/start?device=${encodeURIComponent(navigator.userAgent.slice(0, 100))}`);
};
const ssoReturnTo = (): string => {
  try {
    const to = sessionStorage.getItem(RETURN_KEY);
    sessionStorage.removeItem(RETURN_KEY);
    return to?.startsWith('/oauth/authorize?') ? to : '/';
  } catch {
    return '/';
  }
};

/** The name on the business's single sign-on button, once the business is typed; null when it has none. */
function useSso(business: string) {
  const [label, setLabel] = useState<string | null>(null);
  const slug = business.trim().toLowerCase();
  useEffect(() => {
    setLabel(null);
    if (!/^[a-z0-9-]{2,63}$/.test(slug)) return;
    let live = true;
    const timer = setTimeout(() => {
      request<{ enabled: boolean; label: string | null }>('GET', `/api/auth/sso/${slug}`).then(
        (r) => live && setLabel(r.enabled ? r.label : null),
        () => undefined,
      );
    }, 350);
    return () => {
      live = false;
      clearTimeout(timer);
    };
  }, [slug]);
  return label;
}

/** Where the provider sends the browser back: finishes the sign-in, or says what went wrong. */
export function SsoReturn() {
  const { signInWithTicket } = useSession();
  const [error, setError] = useState<string | null>(null);
  useEffect(() => {
    const hash = new URLSearchParams(window.location.hash.slice(1));
    const ticket = hash.get('ticket');
    // The ticket leaves the address bar (and the history) at once.
    window.history.replaceState(null, '', ticket ? ssoReturnTo() : '/sso');
    if (!ticket) {
      setError(hash.get('error') || t('sso.return_missing'));
      return;
    }
    signInWithTicket(ticket).catch((err) => {
      window.history.replaceState(null, '', '/sso');
      setError(err instanceof ApiError && err.code !== 'offline' ? err.message : t('error.offline'));
    });
  }, [signInWithTicket]);
  return (
    <main className="sso-return-page">
      <section className="card sso-return" aria-live="polite" aria-busy={!error}>
        <span className="brand-mark">
          <span className="brand-logo cube">
            <BrandMark size={30} />
          </span>
          <span className="brand-name">{t('app.name')}</span>
        </span>
        {error ? (
          <>
            <h1>{t('sso.return_failed')}</h1>
            <p className="form-error" role="alert">
              <Icon name="alert" size={16} /> {error}
            </p>
            <Button variant="primary" icon="back" className="w-full" onClick={() => window.location.assign('/')}>
              {t('sso.return_back')}
            </Button>
          </>
        ) : (
          <>
            <h1>{t('sso.return_working')}</h1>
            <p className="muted">{t('sso.return_wait')}</p>
          </>
        )}
      </section>
    </main>
  );
}

/** The left half of the sign-in and sign-up pages: the globe, what ERP PRO promises, and who makes it. */
export function BrandPanel() {
  const wide = useWide();
  return (
    <section className="login-brand">
      <div className="login-globe-glow" aria-hidden="true" />
      {wide ? (
        <Suspense fallback={null}>
          <Globe />
        </Suspense>
      ) : null}
      <div className="login-brand-inner">
        <div className="brand-mark login-logo">
          <span className="brand-logo cube">
            <BrandMark size={40} />
          </span>
          <span className="brand-name">{t('app.name')}</span>
        </div>
        <p className="login-eyebrow">{t('login.eyebrow')}</p>
        <p className="login-slogan">
          <span>{t('login.slogan_1')}</span> <span className="login-slogan-accent">{t('login.slogan_2')}</span>
          <svg className="login-swoosh" viewBox="0 0 320 16" preserveAspectRatio="none" aria-hidden="true" focusable="false">
            <defs>
              <linearGradient id="login-swoosh" x1="0" x2="1" y1="0" y2="0">
                <stop offset="0" stopColor="var(--login-cyan)" stopOpacity="0" />
                <stop offset="0.45" stopColor="var(--login-cyan)" />
                <stop offset="1" stopColor="var(--login-cyan-2)" stopOpacity="0" />
              </linearGradient>
            </defs>
            <path d="M2 14 C 90 1, 230 1, 318 9" fill="none" stroke="url(#login-swoosh)" strokeWidth="3" strokeLinecap="round" />
          </svg>
        </p>
        <p className="login-lead">{t('login.lead')}</p>
        <ul className="login-features">
          {FEATURES.map(([icon, title, body]) => (
            <li key={title}>
              <span className="login-feature-icon">
                <Icon name={icon} size={26} />
              </span>
              <span>
                <strong>{t(title)}</strong>
                <span>{t(body)}</span>
              </span>
            </li>
          ))}
        </ul>
        <ul className="login-modules" aria-label={t('login.modules')}>
          {MODULES.map((m) => (
            <li key={m}>
              <Icon name="check" size={16} /> {t(m)}
            </li>
          ))}
        </ul>
      </div>
      <OwnerLine />
    </section>
  );
}
